import { message as toaster } from 'antd';
import { isEmpty, startCase } from 'lodash';
import qs from 'qs';
import { store } from 'store';
import { hideLoader, showLoader } from 'store/appSlice';
import { handleLogout } from 'api/authentication';
import axios from './api.config';
import { isValidAPIRequest, findType } from 'utils/acl';
import { buildVersionedUrl, DEFAULT_API_VERSION } from './api.constants';

const EXCEPTION_URLS = ['/login', '/reset-password', '/get-signed-url', '/bulk-upload'];

class ApiRequestClass {
	constructor() {
		if (ApiRequestClass._instance) {
			return ApiRequestClass._instance;
		}
		ApiRequestClass._instance = this;
	}

	/**
	 * @param {object} options
	 * @param {string} options.url - Request path (e.g. '/search', '/api/v2/create')
	 * @param {object} [options.data]
	 * @param {boolean} [options.showLoading]
	 * @param {string} [options.method] - 'get' | 'post' | 'put' | 'patch' | 'delete'
	 * @param {object} [options.queryParams]
	 * @param {'v1'|'v2'} [options.version] - API version; when set, URL is built with /api/v1/ or /api/v2/. Omit to keep url unchanged.
	 * @param {boolean} [options.useDefaultVersion] - When true and url has no version segment, use DEFAULT_API_VERSION
	 */
	async common({
		url,
		data = {},
		showLoading = true,
		method = 'post',
		queryParams = {},
		setErrors = undefined,
		config,
		aclBlockApiCall = true,
		version = undefined,
	}) {
		if (showLoading) {
			this.handleShowLoader();
		}
		try {
			const URL = this.addQueryParamsWithUrl(url, queryParams);

			/**
			 * ACL API validation before hitting to server
			 */

			if (
				aclBlockApiCall &&
				!EXCEPTION_URLS?.includes(URL) &&
				!isValidAPIRequest(URL, data, version)
			) {
				const type = findType(URL);
				console.log(!EXCEPTION_URLS?.includes(URL), !isValidAPIRequest(URL, data));
				toaster.error(`You are not authorized to access ${data?.[type]}`);
				return Promise.resolve();
			}
			if (!aclBlockApiCall && method === 'put') {
				axios.headers = {
					'Content-Type': 'application/octet-stream',
				};
			}

			if (version) {
				const URLWithVersion = buildVersionedUrl(URL, version);
				const response = await axios[method](URLWithVersion, data, config);
				if (response.status === 204) {
					throw new Error('No data available to download!');
				}
				return Promise.resolve(response.data);
			}

			const URLWithDefaultVersion = buildVersionedUrl(URL, DEFAULT_API_VERSION);
			const response = await axios[method](URLWithDefaultVersion, data, config);
			return Promise.resolve(response.data);
		} catch (error) {
			this.handleErrors(error, setErrors);
			return Promise.reject(error);
		} finally {
			this.handleHideLoader();
		}
	}

	addQueryParamsWithUrl(url, queryParams) {
		Object.keys(queryParams).forEach(
			(key) => queryParams[key] === null && delete queryParams[key]
		);
		return url + qs.stringify(queryParams, { addQueryPrefix: true });
	}

	// eslint-disable-next-line sonarjs/cognitive-complexity
	handleErrors(error, setErrors) {
		if (error) {
			try {
				// console.log('error handled', error);
				const { data = {} } = error?.response || {};
				// console.log('error data', data);
				const { status } = error.response;
				// console.log('error status', status);
				const checkNetworkError = JSON.stringify(error);
				const NetworkError = 'Network Error';

				if (checkNetworkError.includes(NetworkError)) {
					toaster.error(NetworkError);
					return false;
				}
				if (
					data?.error?.message?.includes(
						'Client reference numbers not found in the file'
					) ||
					data?.error?.message?.includes('Error in updating proof of delivery')
				) {
					return '';
				}
				if (status === 500) {
					toaster.error(error?.response?.data?.error?.message || error.message, [9]);
				} else if (status === 401) {
					this.handle401Error();
				} else if (status === 404) {
					this.handle404Error();
					toaster.error(data?.error?.message);
				} else if (status === 422) {
					/**
					 * 422 error is form validation error
					 */
					console.log(data?.error?.message);
					this.handle422Error(data?.error, setErrors);
				} else {
					this.handleCommonErrors(data);
				}
			} catch (e) {
				this.handleCatchErrors(e, error);
			}
		}
		return null;
	}

	handleCatchErrors(e, error) {
		if (error.toString().includes('No data available')) {
			toaster.info(error.toString());
		} else if (error.toString().includes('timeout')) {
			toaster.error('API Timeout');
		} else {
			toaster.error('Something went wrong please try again');
			console.log('Unhandled error', e);
		}
	}

	handle401Error() {
		/**
		 * 401 is authentication error like session failure
		 */
		toaster.error('Login expired, Please Login');
		setTimeout(() => {
			console.log('redirectToLogin', handleLogout);
			handleLogout();
		}, 3000);
	}

	handleCommonErrors(data) {
		let error = '404 - Resource Not Found';
		if (data?.error?.message) {
			error = data?.error?.message;
		}
		toaster.error(error);
	}

	handleShowLoader() {
		store.dispatch(showLoader());
	}

	handleHideLoader() {
		store.dispatch(hideLoader());
	}

	handle422Error(serverErrors, setErrors) {
		console.log('serverErrors', serverErrors.message);
		if (typeof serverErrors?.message === 'string') {
			toaster.error(serverErrors?.message);
			// Don't try to set form field errors for string messages
			return serverErrors;
		}

		// Only set form field errors if message is an object (field-specific errors)
		if (!isEmpty(serverErrors) && setErrors && typeof serverErrors?.message === 'object') {
			const formErrors = {};
			Object.entries(serverErrors?.message).forEach(([key, value]) => {
				formErrors[key] = `${startCase(key)} ${value}`;
			});
			// serverErrors?.missing?.forEach((param) => {
			// 	formErrors[param] = `${capitalize(param)} is Required`;
			// });
			setErrors(formErrors);
			return serverErrors;
		}
		return null;
	}

	handle404Error(serverErrors, setErrors) {
		// Only set form field errors if message is an object (field-specific errors)
		if (!isEmpty(serverErrors) && setErrors && typeof serverErrors?.message === 'object') {
			const formErrors = {};
			Object.entries(serverErrors?.message).forEach(([key, value]) => {
				formErrors[key] = `${startCase(key)} ${value}`;
			});
			setErrors(formErrors);
			return serverErrors;
		}
		return null;
	}
}

export const API = new ApiRequestClass();
